import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { getEmployees } from '../services/employeeService';
import type { EmployeeListFilters, EmployeeListItem } from '../types/employee';
import { subscribeEmployeesChanged } from '../utils/employeeListSync';
import { useRefreshOnFocus } from './useRefreshOnFocus';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

export function useEmployees() {
  const [items, setItems] = useState<EmployeeListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearchState] = useState('');
  const [filters, setFiltersState] = useState<EmployeeListFilters>({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const mounted = useRef(true);
  const requestId = useRef(0);
  const loadingMore = useRef(false);
  const pageRef = useRef(1);
  const searchRef = useRef('');
  const filtersRef = useRef<EmployeeListFilters>({});
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPage = useCallback(async (nextPage: number, mode: 'replace' | 'append' | 'refresh') => {
    const currentRequest = ++requestId.current;
    if (mode === 'append') {
      if (loadingMore.current) return;
      loadingMore.current = true;
      setIsLoadingMore(true);
    } else if (mode === 'refresh') {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const result = await getEmployees({
        page: nextPage,
        pageSize: DEFAULT_PAGE_SIZE,
        filters: {
          ...filtersRef.current,
          search: searchRef.current.trim() || undefined,
        },
      });

      if (!mounted.current || currentRequest !== requestId.current) return;

      setPage(result.page);
      pageRef.current = result.page;
      setHasMore(result.page < result.totalPages);
      setItems(current => {
        if (mode === 'append') {
          const seen = new Set(current.map(item => item.employeeId));
          const merged = [...current];
          for (const item of result.items) {
            if (!seen.has(item.employeeId)) merged.push(item);
          }
          return merged;
        }
        return result.items;
      });
    } catch {
      if (mounted.current && currentRequest === requestId.current) {
        setError('Unable to load employees.');
      }
    } finally {
      loadingMore.current = false;
      if (mounted.current && currentRequest === requestId.current) {
        setIsLoading(false);
        setIsRefreshing(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchPage(1, 'replace');
    return () => {
      mounted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [fetchPage]);

  useRefreshOnFocus(() => {
    fetchPage(1, 'refresh').catch(() => undefined);
  });

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    return subscribeEmployeesChanged(() => {
      fetchPage(1, 'refresh').catch(() => undefined);
    });
  }, [fetchPage]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      searchRef.current = value;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        pageRef.current = 1;
        fetchPage(1, 'replace');
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchPage],
  );

  const setFilters = useCallback(
    (next: EmployeeListFilters) => {
      setFiltersState(next);
      filtersRef.current = next;
      pageRef.current = 1;
      fetchPage(1, 'replace');
    },
    [fetchPage],
  );

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore.current || isLoading || isRefreshing) return;
    fetchPage(pageRef.current + 1, 'append');
  }, [fetchPage, hasMore, isLoading, isRefreshing]);

  return {
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    search,
    filters,
    page,
    hasMore,
    loadEmployees: () => fetchPage(1, 'replace'),
    refreshEmployees: () => fetchPage(1, 'refresh'),
    loadMore,
    setSearch,
    setFilters,
    retry: () => fetchPage(1, 'replace'),
  };
}
